import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, Message, Placeholder, SelectPicker, Tooltip, Whisper } from 'rsuite';
import { ConfirmModal, FilterField, FilterToolbar } from '@nsi-advisory-services/components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../utils/toast';
import TimeOffRequestCard from '../components/timeoff/TimeOffRequestCard';
import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import {
  useCancelTimeOffRequest,
  useEmployeePato,
  useEmployees,
  usePullTimeCampHours,
  useTimeOffRequests
} from '../hooks/useQueries';
import { PAYROLL_ENABLED } from '../config/payrollFeature';

// TimeCamp "Time Off (Paid)" task id — used to tally actual time-off hours logged.
const TIME_OFF_TASK_ID = '51054114';

// An approved request carrying this sentinel reason was auto-"Entered" (hours
// reduction — not subject to approval), NOT actually approved. Same value the
// status badge uses to render the "Entered" vs "Approved" tag.
const NO_APPROVAL_REQUIRED_REASON = 'no approval required';
// True for the auto-"Entered" (reduction) requests, which should be excluded
// from the approved-hours total.
const isEnteredRequest = (request) =>
  String(request?.status || '').toLowerCase() === 'approved' &&
  String(request?.approval_reason || '').trim().toLowerCase() === NO_APPROVAL_REQUIRED_REASON;
// "PATO" with a hover tooltip spelling out the acronym.
const PatoTerm = () => (
  <Whisper placement="top" trigger="hover" speaker={<Tooltip>Paid Any Time Off</Tooltip>}>
    <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>PATO</span>
  </Whisper>
);

// Auto-refresh the TimeCamp time-off tally if the cached value is older than this.
const TIME_OFF_STALE_MS = 14 * 24 * 60 * 60 * 1000; // 2 weeks
const TIME_OFF_CACHE_KEY = 'timeoff_timecamp_hours_v1';

// Per-year localStorage cache of the TimeCamp time-off tally + when it was pulled.
const readTimeOffCache = () => {
  try {
    const raw = localStorage.getItem(TIME_OFF_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};
const writeTimeOffCache = (cache) => {
  try {
    localStorage.setItem(TIME_OFF_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* ignore quota / private-mode errors */
  }
};

const formatRefreshedAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const parseLocalDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const countWeekdaysInclusive = (startDate, endDate) => {
  if (!startDate || !endDate || endDate < startDate) return 0;
  let count = 0;
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const day = cursor.getDay();
    if (day >= 1 && day <= 5) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const formatHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
};

const compareByStartDateAscending = (a, b) => {
  const aStart = parseLocalDateOnly(a?.start_date);
  const bStart = parseLocalDateOnly(b?.start_date);

  const aStartTime = aStart ? aStart.getTime() : Number.POSITIVE_INFINITY;
  const bStartTime = bStart ? bStart.getTime() : Number.POSITIVE_INFINITY;
  if (aStartTime !== bStartTime) return aStartTime - bStartTime;

  const aEnd = parseLocalDateOnly(a?.end_date);
  const bEnd = parseLocalDateOnly(b?.end_date);
  const aEndTime = aEnd ? aEnd.getTime() : Number.POSITIVE_INFINITY;
  const bEndTime = bEnd ? bEnd.getTime() : Number.POSITIVE_INFINITY;
  if (aEndTime !== bEndTime) return aEndTime - bEndTime;

  return String(a?.id || '').localeCompare(String(b?.id || ''));
};

// Upcoming includes time off already under way; past means it has ended.
const TIMING_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Past', value: 'past' },
];

export default function TimeOffManagePage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const requestsQuery = useTimeOffRequests({ enabled: true });
  const cancelRequest = useCancelTimeOffRequest();

  // Admins can look at - and edit - anybody's requests. Whose is on screen
  // lives in the URL (?employee=<id>) so a link or a refresh keeps it.
  const isAdmin = auth.isAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const selfId = String(auth.employee?.id || '');
  const viewEmployeeId = isAdmin && searchParams.get('employee') ? String(searchParams.get('employee')) : selfId;
  const viewingOther = Boolean(viewEmployeeId) && viewEmployeeId !== selfId;
  const employeesQuery = useEmployees({ enabled: isAdmin });
  const employeeOptions = useMemo(() => {
    const rows = (employeesQuery.data || []).filter((row) => row?.is_active !== false || String(row?.id) === viewEmployeeId);
    return rows
      .map((row) => {
        const name = `${String(row?.first_name || '').trim()} ${String(row?.last_name || '').trim()}`.trim() || String(row?.email || '') || 'Unknown';
        return { value: String(row.id), label: String(row.id) === selfId ? `${name} (Me)` : name };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [employeesQuery.data, selfId, viewEmployeeId]);
  const viewEmployeeName = employeeOptions.find((o) => o.value === viewEmployeeId)?.label || null;
  const setViewEmployee = (id) => {
    const next = new URLSearchParams(searchParams);
    if (!id || String(id) === selfId) next.delete('employee');
    else next.set('employee', String(id));
    setSearchParams(next, { replace: true });
  };

  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [timing, setTiming] = useState('all');
  const [search, setSearch] = useState('');

  const requestOverlapsYear = (request, year) => {
    const start = parseLocalDateOnly(request?.start_date);
    const end = parseLocalDateOnly(request?.end_date);
    if (!start || !end || end < start) return false;

    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    return start <= yearEnd && end >= yearStart;
  };

  const hoursForOverlapInYear = (request, year) => {
    const start = parseLocalDateOnly(request.start_date);
    const end = parseLocalDateOnly(request.end_date);
    if (!start || !end || end < start) return 0;

    const yearStart = new Date(year, 0, 1);
    const yearEnd = new Date(year, 11, 31);
    const overlapStart = start > yearStart ? start : yearStart;
    const overlapEnd = end < yearEnd ? end : yearEnd;
    if (overlapEnd < overlapStart) return 0;

    const totalHoursRaw = Number(request.total_hours);
    const hasStoredHours = Number.isFinite(totalHoursRaw) && totalHoursRaw >= 0;

    const startMinutes = Number(request.start_minutes);
    const endMinutes = Number(request.end_minutes);
    const hasMinuteRange = Number.isFinite(startMinutes) && Number.isFinite(endMinutes) && endMinutes > startMinutes;

    const isSingleDay = start.getTime() === end.getTime();
    if (isSingleDay && hasMinuteRange) {
      const overlapsSingleDay = overlapStart.getTime() === start.getTime() && overlapEnd.getTime() === end.getTime();
      return overlapsSingleDay ? (endMinutes - startMinutes) / 60 : 0;
    }

    if (hasStoredHours) {
      const totalWeekdays = countWeekdaysInclusive(start, end);
      const overlapWeekdays = countWeekdaysInclusive(overlapStart, overlapEnd);
      if (totalWeekdays <= 0 || overlapWeekdays <= 0) return 0;
      return (totalHoursRaw * overlapWeekdays) / totalWeekdays;
    }

    return countWeekdaysInclusive(overlapStart, overlapEnd) * 8;
  };

  const myRequests = useMemo(() => {
    if (!viewEmployeeId) return [];
    return (requestsQuery.data || []).filter((request) => String(request.employee_id) === viewEmployeeId);
  }, [viewEmployeeId, requestsQuery.data]);

  const yearFilteredRequests = useMemo(
    () => myRequests.filter((request) => requestOverlapsYear(request, selectedYear)),
    [myRequests, selectedYear]
  );

  // What the lists show. The year metrics below stay on yearFilteredRequests.
  const listedRequests = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const term = search.trim().toLowerCase();
    return yearFilteredRequests.filter((request) => {
      if (timing !== 'all') {
        const end = parseLocalDateOnly(request?.end_date);
        if (!end || (timing === 'past' ? end >= today : end < today)) return false;
      }
      if (!term) return true;
      return [request?.notes, request?.request_type, request?.status, request?.approval_reason]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [yearFilteredRequests, timing, search]);

  // Every year the employee has actually booked time off in, newest first, plus
  // this one and the year already selected — a list that offered nothing for the
  // year on screen, or nothing at all to someone with no requests yet, would be
  // a filter that can't be used.
  const yearOptions = useMemo(() => {
    const years = new Set([new Date().getFullYear(), selectedYear]);

    myRequests.forEach((request) => {
      [request?.start_date, request?.end_date].forEach((value) => {
        const year = Number(String(value || '').slice(0, 4));
        if (Number.isFinite(year) && year > 1900) years.add(year);
      });
    });

    return [...years]
      .sort((a, b) => b - a)
      .map((year) => ({ label: String(year), value: year }));
  }, [myRequests, selectedYear]);

  const pendingRequests = useMemo(
    () => [...listedRequests
      .filter((request) => String(request.status || '').toLowerCase() === 'pending')]
      .sort(compareByStartDateAscending),
    [listedRequests]
  );

  const approvedRequests = useMemo(
    () => [...listedRequests
      .filter((request) => String(request.status || '').toLowerCase() === 'approved')]
      .sort(compareByStartDateAscending),
    [listedRequests]
  );

  // Split the approved bucket into truly-approved requests (which draw down the
  // PTO balance) and the auto-"Entered" reduction requests (logged for
  // availability only), so each gets its own collapsible section.
  const approvedOnlyRequests = useMemo(
    () => approvedRequests.filter((request) => !isEnteredRequest(request)),
    [approvedRequests]
  );
  const enteredRequests = useMemo(
    () => approvedRequests.filter((request) => isEnteredRequest(request)),
    [approvedRequests]
  );

  const rejectedRequests = useMemo(
    () => [...listedRequests
      .filter((request) => String(request.status || '').toLowerCase() === 'rejected')]
      .sort(compareByStartDateAscending),
    [listedRequests]
  );

  const pendingHoursTotal = useMemo(
    () => pendingRequests.reduce((sum, request) => sum + hoursForOverlapInYear(request, selectedYear), 0),
    [pendingRequests, selectedYear]
  );

  // Truly-approved hours only (excludes auto-"Entered" reduction time).
  const approvedOnlyHoursTotal = useMemo(
    () => approvedRequests
      .filter((request) => !isEnteredRequest(request))
      .reduce((sum, request) => sum + hoursForOverlapInYear(request, selectedYear), 0),
    [approvedRequests, selectedYear]
  );
  // For the year metric, which ignores the Upcoming/Past filter.
  const yearApprovedOnlyHoursTotal = useMemo(
    () => yearFilteredRequests
      .filter((request) => String(request.status || '').toLowerCase() === 'approved' && !isEnteredRequest(request))
      .reduce((sum, request) => sum + hoursForOverlapInYear(request, selectedYear), 0),
    [yearFilteredRequests, selectedYear]
  );
  // Auto-"Entered" (reduction) hours — logged for availability, not approved.
  const enteredHoursTotal = useMemo(
    () => approvedRequests
      .filter((request) => isEnteredRequest(request))
      .reduce((sum, request) => sum + hoursForOverlapInYear(request, selectedYear), 0),
    [approvedRequests, selectedYear]
  );

  const rejectedHoursTotal = useMemo(
    () => rejectedRequests.reduce((sum, request) => sum + hoursForOverlapInYear(request, selectedYear), 0),
    [rejectedRequests, selectedYear]
  );

  // Actual Time Off hours logged in TimeCamp for the selected year (task 51054114).
  // Accounts for employees approved for time off who don't end up taking it all.
  // Cached per-year in localStorage with the pull timestamp so we only auto-pull
  // when the value is missing or older than two weeks.
  const pullTimeCampHours = usePullTimeCampHours();
  const [timeOffCache, setTimeOffCache] = useState(() => readTimeOffCache());
  const timeOffEntry = timeOffCache[String(selectedYear)] || null;
  const timeCampTimeOffHours = Number(timeOffEntry?.hours) || 0;

  /*
    What is left of this year's PATO.

    Allowance AND usage both come from the endpoint, deliberately - not from
    timeCampTimeOffHours above.

    That figure is a browser-cached pull that has not necessarily happened yet:
    before the first refresh timeOffEntry is null and it reads 0, which is
    indistinguishable from having taken none. Subtracting it would then show a
    full allowance as remaining and state "0.00 of 80.00 hrs used" - a wrong
    number presented with no hint that it is wrong, on the one figure somebody
    books time off against. The tile above it already guards for this, which is
    why it prints an em dash rather than a zero until the pull lands.

    The endpoint reads TimeCamp for this employee and this year on the way past
    and answers null when it could not, so unknown stays unknown. It is also the
    same source the /me panel uses, so the two cannot disagree.

    Behind the payroll flag because the allowance lives in the payroll module.
    The endpoint lets somebody read their own row without any payroll
    permission, so this needs no extra grant - see requireElectionAccess.
  */
  const patoQuery = useEmployeePato(viewEmployeeId, selectedYear, {
    enabled: PAYROLL_ENABLED && Boolean(viewEmployeeId),
  });

  // Unlimited time off has no allowance and nothing remaining - only usage.
  const patoUnlimited = Boolean(patoQuery.data?.unlimited);
  const patoAllowance = Number(patoQuery.data?.hours ?? NaN);
  const hasPatoAllowance = Number.isFinite(patoAllowance);
  // "Used of allowance" reads against what the balance is measured on, which
  // is the year's usage unless an unlimited stretch reset it partway.
  const patoUsed = patoQuery.data?.drawnHours ?? patoQuery.data?.usedHours ?? null;
  const patoUsedYear = patoQuery.data?.usedHours ?? null;
  const patoRemaining = patoQuery.data?.remainingHours ?? null;
  // Both, not either: a remaining figure with no usage behind it is the exact
  // state this tile must not present as a balance.
  const hasPatoUsage = hasPatoAllowance && patoUsed != null && patoRemaining != null;
  const timeOffRefreshedLabel = formatRefreshedAt(timeOffEntry?.refreshedAt);
  // Guards against overlapping/duplicate auto-pulls (e.g. React strict-mode).
  const pullingYearsRef = useRef(new Set());

  const pullTimeOffHours = useCallback(async (year, { silent = false } = {}) => {
    const key = String(year);
    if (pullingYearsRef.current.has(key)) return;
    pullingYearsRef.current.add(key);
    try {
      const data = await pullTimeCampHours.mutateAsync({
        startDate: `${year}-01-01`,
        endDate: `${year}-12-31`,
        taskId: TIME_OFF_TASK_ID,
      });
      const hours = Number(data?.totalHours) || 0;
      const refreshedAt = new Date().toISOString();
      setTimeOffCache((prev) => {
        const next = { ...prev, [key]: { hours, refreshedAt } };
        writeTimeOffCache(next);
        return next;
      });
      showToast('success', `Time off hours for ${year} updated: ${hours.toFixed(2)} h.`);
    } catch (err) {
      if (!silent) showToast('error', String(err?.message || err) || 'Could not refresh time off hours.');
    } finally {
      pullingYearsRef.current.delete(key);
    }
  }, [pullTimeCampHours]);

  // Auto-pull on open / year change when the cached tally is missing or stale.
  // The TimeCamp tally only reads the signed-in person's own hours.
  useEffect(() => {
    if (!auth.employee?.id || viewingOther) return;
    const entry = timeOffCache[String(selectedYear)];
    const age = entry?.refreshedAt ? Date.now() - new Date(entry.refreshedAt).getTime() : Infinity;
    if (age > TIME_OFF_STALE_MS) pullTimeOffHours(selectedYear, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, auth.employee?.id, viewingOther]);

  const onView = (row) => navigate(`/time-off/${row.id}`);

  const onWithdraw = (row) => {
    setWithdrawTarget(row);
    setWithdrawOpen(true);
  };

  // Your own pending request is modified by resubmitting it, which the
  // confirm step explains. An admin editing someone else's request, or any
  // approved one, changes it in place - no confirm about resubmitting.
  const onEdit = (row) => {
    const isOwn = String(row.employee_id) === selfId;
    const isPending = String(row.status || '').toLowerCase() === 'pending';
    if (isAdmin && (!isOwn || !isPending)) {
      navigate(`/time-off/create?editRequestId=${encodeURIComponent(String(row.id))}`);
      return;
    }
    setEditTarget(row);
    setEditOpen(true);
  };

  const confirmWithdraw = async () => {
    if (!withdrawTarget?.id) return;
    await cancelRequest.mutateAsync(withdrawTarget.id);
    setWithdrawOpen(false);
    setWithdrawTarget(null);
  };

  const confirmEdit = () => {
    if (!editTarget?.id) return;
    setEditOpen(false);
    navigate(`/time-off/create?editRequestId=${encodeURIComponent(String(editTarget.id))}`);
  };

  const withdrawStatus = String(withdrawTarget?.status || '').toLowerCase();
  const withdrawIsApproved = withdrawStatus === 'approved';

  if (requestsQuery.isLoading) return <Placeholder.Paragraph rows={6} active />;

  if (requestsQuery.error) {
    return (
      <Message type="error" header="Error" showIcon>
        {String(requestsQuery.error?.message || requestsQuery.error)}
      </Message>
    );
  }

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Time Off', to: '/time-off' },
          { label: 'My Time Off' },
        ]}
        icon="bi-calendar-check"
        title="My Time Off"
        note={viewingOther && viewEmployeeName ? `for ${viewEmployeeName}` : null}
        actions={(
          <>
            <button
              type="button"
              className="bdc-btn bdc-btn--primary"
              onClick={() => navigate('/time-off/create')}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" />
              New Request
            </button>
          </>
        )}
      />

      <FilterToolbar>
        <FilterField label="Search" icon="bi-search" basis={260}>
          <Input
            type="search"
            value={search}
            onChange={setSearch}
            placeholder="Notes, type, status"
            aria-label="Search time off requests"
          />
        </FilterField>
        {isAdmin ? (
          <FilterField label="Employee" basis={240} max={320}>
            <SelectPicker
              data={employeeOptions}
              value={viewEmployeeId || null}
              onChange={(value) => setViewEmployee(value)}
              loading={employeesQuery.isLoading}
              cleanable={false}
              searchable
              placeholder="Select employee"
              block
            />
          </FilterField>
        ) : null}
        <FilterField label="Year" basis={160} max={200}>
          <SelectPicker
            data={yearOptions}
            value={selectedYear}
            onChange={(value) => setSelectedYear(value ?? new Date().getFullYear())}
            cleanable={false}
            searchable={false}
            block
          />
        </FilterField>
        <FilterField label="When" basis={160} max={200}>
          <SelectPicker
            data={TIMING_OPTIONS}
            value={timing}
            onChange={(value) => setTiming(value || 'all')}
            cleanable={false}
            searchable={false}
            block
          />
        </FilterField>
      </FilterToolbar>

      <Section
        title="Pending"
        count={pendingRequests.length}
        collapsible
        defaultOpen
        aside={(
          <span className="bdc-section__note bdc-num">
            {formatHours(pendingHoursTotal)} hrs
          </span>
        )}
      >
        {pendingRequests.length === 0 ? (
          <div className="text-muted">No pending requests.</div>
        ) : (
          <div className="row g-2">
            {pendingRequests.map((request) => (
              <div key={request.id} className="col-12 col-md-6 col-xl-3">
                <TimeOffRequestCard
                  request={request}
                  onClick={onView}
                  onEdit={onEdit}
                  onWithdraw={onWithdraw}
                />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Approved"
        count={approvedOnlyRequests.length}
        note="Hours applied to your PTO balance"
        collapsible
        aside={(
          <span className="bdc-section__note bdc-num">
            {formatHours(approvedOnlyHoursTotal)} hrs
          </span>
        )}
      >
        {approvedOnlyRequests.length === 0 ? (
          <div className="text-muted">No approved requests.</div>
        ) : (
          <div className="row g-2">
            {approvedOnlyRequests.map((request) => (
              <div key={request.id} className="col-12 col-md-6 col-xl-3">
                <TimeOffRequestCard
                  request={request}
                  onClick={onView}
                  onEdit={onEdit}
                  onWithdraw={onWithdraw}
                />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Entered"
        count={enteredRequests.length}
        collapsible
        aside={(
          <span className="bdc-section__note bdc-num">
            {formatHours(enteredHoursTotal)} hrs
          </span>
        )}
      >
        {enteredRequests.length === 0 ? (
          <div className="text-muted">No entered requests.</div>
        ) : (
          <div className="row g-2">
            {enteredRequests.map((request) => (
              <div key={request.id} className="col-12 col-md-6 col-xl-3">
                <TimeOffRequestCard
                  request={request}
                  onClick={onView}
                  onEdit={onEdit}
                  onWithdraw={onWithdraw}
                />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Rejected"
        count={rejectedRequests.length}
        collapsible
        aside={(
          <span className="bdc-section__note bdc-num">
            {formatHours(rejectedHoursTotal)} hrs
          </span>
        )}
      >
        {rejectedRequests.length === 0 ? (
          <div className="text-muted">No rejected requests.</div>
        ) : (
          <div className="row g-2">
            {rejectedRequests.map((request) => (
              <div key={request.id} className="col-12 col-md-6 col-xl-3">
                <TimeOffRequestCard
                  request={request}
                  onClick={onView}
                />
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Totals"
        note={selectedYear}
        aside={viewingOther ? null : (
          /* The refresh belongs to the year, not to one figure in it. */
          <span className="d-flex align-items-center gap-2">
            <span className="bdc-section__note">
              {timeOffRefreshedLabel ? `Refreshed ${timeOffRefreshedLabel}` : 'Not refreshed yet.'}
            </span>
            <button
              type="button"
              className="bdc-iconbtn"
              title={`Refresh ${selectedYear} time off hours from TimeCamp`}
              aria-label={`Refresh ${selectedYear} time off hours from TimeCamp`}
              onClick={() => pullTimeOffHours(selectedYear)}
              disabled={pullTimeCampHours.isPending}
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            </button>
          </span>
        )}
      >

        <div className="bdc-metrics">
          <div className="bdc-metric bdc-metric--wide">
            <div className="bdc-metric__body">
              <div>
                <p className="bdc-metric__label">Approved Hours (Year)</p>
                <p className="bdc-metric__value bdc-num">
                  {formatHours(yearApprovedOnlyHoursTotal)} hrs
                </p>
                <div className="bdc-metric__foot">
                  Entered into Employee Connect, not counted against your <PatoTerm /> balance
                </div>
              </div>
            </div>
          </div>

          {/* TimeCamp only gives the signed-in person's own hours. */}
          {!viewingOther ? (
            <div className="bdc-metric bdc-metric--wide">
              <div className="bdc-metric__body">
                <div>
                  <p className="bdc-metric__label">Time Off Taken (TimeCamp)</p>
                  <p className="bdc-metric__value bdc-num">
                    {timeOffEntry
                      ? `${formatHours(timeCampTimeOffHours)} hrs`
                      : pullTimeCampHours.isPending ? '…' : '—'}
                  </p>
                  <div className="bdc-metric__foot">
                    Actual hours logged against your <PatoTerm /> balance
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Last of the three, because it is the two above subtracted: the
              allowance for the year less what TimeCamp says has been taken.
              Approved hours are not in it - those are entered rather than
              drawn, which is what the first tile's footnote says. */}
          {PAYROLL_ENABLED ? (
            <div className="bdc-metric bdc-metric--full">
              <div className="bdc-metric__body">
                <div>
                  <p className="bdc-metric__label"><PatoTerm /> Remaining</p>
                  <p
                    className={`bdc-metric__value bdc-num${
                      patoRemaining != null && patoRemaining < 0 ? ' bdc-text-bad' : ''
                    }`}
                  >
                    {patoQuery.isPending
                      ? '…'
                      : patoUnlimited ? 'Unlimited'
                        : hasPatoUsage ? `${formatHours(patoRemaining)} hrs` : '—'}
                  </p>
                  <div className="bdc-metric__foot">
                    {patoUnlimited ? (
                      // Still worth saying what was used: it is recorded, and
                      // it is what anyone asking "how much have I taken" wants.
                      patoUsedYear == null
                        ? 'Unlimited time off under the company policy'
                        : `Unlimited time off under the company policy · ${formatHours(patoUsedYear)} hrs used`
                    ) : hasPatoUsage ? (
                      <>
                        {formatHours(patoUsed)} of {formatHours(patoAllowance)} hrs used
                        {patoRemaining < 0 ? ' · over your balance' : ''}
                        {/* Said on the tile rather than left to a tooltip: the
                            figure is a live read of a period still being
                            worked, so it can move until payroll settles it, and
                            anyone booking time off against it needs to know
                            that before they act on it. */}
                        <div className="timeoff-pato-caveat">
                          Only accurate as of the end of the current pay period, once payroll has been run
                          for it. Check your balance against your paystub to be sure.
                        </div>
                      </>
                    ) : patoQuery.isError ? (
                      'Balance unavailable right now'
                    ) : hasPatoAllowance ? (
                      // The allowance is known but the draw-down is not. Said
                      // plainly, with the allowance, rather than quietly
                      // showing it as though nothing had been taken.
                      `Allowance is ${formatHours(patoAllowance)} hrs. What has been taken could not be read `
                        + 'from TimeCamp, so the balance is not shown.'
                    ) : (
                      'Your allowance for the year, less what has been taken'
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Section>

      {/* Named as the card's button is: an approved request is deleted,
          a pending one withdrawn. */}
      <ConfirmModal
        open={withdrawOpen}
        title={withdrawIsApproved ? 'Delete Request' : 'Withdraw Request'}
        body={withdrawIsApproved
          ? 'Delete this approved time-off request? It will also be removed from the company calendar.'
          : 'Withdraw this time-off request? The pending approval will be canceled.'}
        confirmText={withdrawIsApproved ? 'Delete' : 'Withdraw'}
        cancelText="Close"
        danger
        loading={cancelRequest.isPending}
        onConfirm={confirmWithdraw}
        onCancel={() => setWithdrawOpen(false)}
      />

      <ConfirmModal
        open={editOpen}
        title="Edit Pending Request"
        body="This will cancel the existing pending approval and create a new modified request. Continue?"
        confirmText="Continue"
        size="sm"
        onConfirm={confirmEdit}
        onCancel={() => setEditOpen(false)}
      />

    </div>
  );
}
